/* eslint-disable no-restricted-imports -- firebase compat: 新たにコードを追加する時は modular API を使う。modular API へ移行したらこのコメントごと削除する */
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
/* eslint-enable no-restricted-imports */
import { useEffect, useState } from "react";
import { z } from "zod";

import type { MyCartData } from "modules/cart";

// Firestore schemas for validation
const BonusMediaAttachmentSchema = z.object({
  _id: z.string(),
  createdAt: z.nullable(z.instanceof(firebase.firestore.Timestamp)),
  eventId: z.string(),
  ticketId: z.string(),
  releaseAt: z.nullable(z.instanceof(firebase.firestore.Timestamp)),
  expiredAt: z.nullable(z.instanceof(firebase.firestore.Timestamp)),
});

const BonusMediaSchema = z.object({
  _id: z.string(),
  bonusName: z.string(),
  createdAt: z.nullable(z.instanceof(firebase.firestore.Timestamp)),
  filename: z.string(),
});

type BonusMediaAttachment = z.infer<typeof BonusMediaAttachmentSchema>;
type BonusMedia = z.infer<typeof BonusMediaSchema>;

type TicketBonusMedia = BonusMedia & {
  releaseAt: firebase.firestore.Timestamp | null;
  expiredAt: firebase.firestore.Timestamp | null;
};

const fetchBonusAttachment = async (
  eventId: string,
  ticketId: string
): Promise<BonusMediaAttachment[]> => {
  const collectionPath = `/tickets/${eventId}/tickets/${ticketId}/bonus`;
  const snapshot = await firebase.firestore().collection(collectionPath).get();

  const results: BonusMediaAttachment[] = [];

  for (const doc of snapshot.docs) {
    try {
      const data = { ...doc.data(), _id: doc.id };
      const validated = BonusMediaAttachmentSchema.parse(data);
      results.push(validated);
    } catch (error) {
      console.error(
        `Zod validation failed for BonusMediaAttachment (${doc.id}):`,
        error
      );
      // Skip invalid documents - continue processing valid ones
    }
  }

  return results;
};

const fetchBonusMedia = async (
  mediaId: string
): Promise<BonusMedia | undefined> => {
  const docPath = `/bonusMedia/${mediaId}`;
  const snapshot = await firebase.firestore().doc(docPath).get();

  if (!snapshot.exists) {
    console.warn(`BonusMedia not found: ${mediaId}`);
    return undefined;
  }

  try {
    const data = { ...snapshot.data(), _id: snapshot.id };
    return BonusMediaSchema.parse(data);
  } catch (error) {
    console.error(`Zod validation failed for BonusMedia (${mediaId}):`, error);
    return undefined;
  }
};

const fetchBonusMediasAttachedToTicket = async (
  eventId: string,
  ticketId: string
): Promise<TicketBonusMedia[]> => {
  try {
    const attachments = await fetchBonusAttachment(eventId, ticketId);
    const mediaList: TicketBonusMedia[] = [];

    for (const attachment of attachments) {
      const media = await fetchBonusMedia(attachment._id);

      if (media !== undefined) {
        mediaList.push({
          ...media,
          releaseAt: attachment.releaseAt,
          expiredAt: attachment.expiredAt,
        });
      }
    }

    const filteredMedias = mediaList.filter((media) => {
      if (media.expiredAt === null) return true;
      return media.expiredAt.toDate() > new Date();
    });

    return filteredMedias;
  } catch (e) {
    console.error("Failed to fetch bonus medias attached to ticket", e);
    return [];
  }
};

export type BonusMediaTicketRecord = { [ticketId: string]: BonusMedia[] };

/**
 * カート内のチケットに紐づくボーナスメディアを取得する
 */
export const useFetchBonusMediaAttachedToTicket = (
  pickedMyCart: MyCartData[]
): BonusMediaTicketRecord => {
  const [bonusMediaTicketRecord, setBonusMediaTicketRecord] =
    useState<BonusMediaTicketRecord>({});

  useEffect(() => {
    if (pickedMyCart.length === 0) return;

    const fetchBonusMediaForTickets = async () => {
      const bonusMediaList = (
        await Promise.all(
          pickedMyCart.map(async (cartItem) => {
            return await Promise.all(
              cartItem.tickets.map(async (ticket) => {
                const bonusMedia = await fetchBonusMediasAttachedToTicket(
                  cartItem.eventId,
                  ticket.productId
                );

                return { [ticket.productId]: bonusMedia };
              })
            );
          })
        )
      )
        .flat()
        .flat();

      setBonusMediaTicketRecord(Object.assign({}, ...bonusMediaList));
    };

    void fetchBonusMediaForTickets();
  }, [pickedMyCart]);

  return bonusMediaTicketRecord;
};
